/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useEffect, Fragment, useMemo } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import Popover from "@react/react-spectrum/Popover";
import { setActiveVerbCallback } from "utils/QuickToolsUtils";
import { Flyout } from "components/quickTools/common/Flyout";
import PrimaryPanelCss from "components/quickTools/Primary/PrimaryPanel.css";
import { QUICK_TOOLS_STYLE, cursorType as cursors, closeFlyoutReasons, multidocToastContext } from "constants/enums";
import ButtonWrapper from "dc-preview-utils/src/components/Button/ButtonWrapper";
import { TOOLTIP_RIGHT_PLACEMENT, TOOLTIP_DELAY } from "constants/constants";
import * as stores from "stores";
import { APP_THEMES } from "dc-preview-utils/src/Constants/constants";
import { getLiquidModeApis } from "publicApis/LiquidModeApi";
import { recordDropinQuickToolLoadedTime } from "utils/performance";
import {
  QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT,
  QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT_FOR_TOUCH_DEVICES,
  DISABLED_QT_TOAST_TIMEOUT
} from "constants/constants";
import { isTouchDevice } from "dc-preview-utils/src/Utils/DeviceUtils";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import { getActiveVerb } from "selectors/quickToolsSelectors";
import { trackMultidocModificationDisabledClicked } from "analytics/events";

const QuickToolItem = props => {
  const {
    currentActiveVerb,
    verbDetails,
    flyoutComponent,
    activeVerb,
    isQuickToolDisabled,
    groupIndex,
    disabledStateNotificationMsg,
    disabledStateOpts,
    disabledStateTooltipStr,
    showCaret,
    quickToolClickHelper,
    flyoutIndex,
    closeFlyout,
    cursorType,
    forceShowFlyout,
    currentTheme,
    shouldUseMobileModernViewer,
    initialAutoHide,
    shouldSelectVerbOnClick,
    selectedVerbOnUi,
    isMultiDocModeEnabled,
    shouldShowConditionalToolTip
  } = props;
  const isActiveQuickTool = currentActiveVerb === selectedVerbOnUi;
  const verbIndex = verbDetails.findIndex(({ verbId }) => verbId === currentActiveVerb);
  const showFlyout = groupIndex === flyoutIndex && showCaret && !shouldUseMobileModernViewer;
  const flyoutAutoHideTimeout = isTouchDevice()
    ? QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT_FOR_TOUCH_DEVICES
    : QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT;

  // Trigger Event when Quick Tool is loaded
  useEffect(() => {
    recordDropinQuickToolLoadedTime(currentActiveVerb);
  }, []);

  const quickToolClickHandler = event => {
    const { notificationViewStore, documentStore } = stores;
    if (isQuickToolDisabled) {
      if (isMultiDocModeEnabled && disabledStateTooltipStr) {
        trackMultidocModificationDisabledClicked(`${multidocToastContext.QUICK_TOOLS}:${currentActiveVerb}`);
        return;
      }
      disabledStateNotificationMsg &&
        notificationViewStore.addNotification(disabledStateNotificationMsg, {
          shouldRemoveActiveToasts: true,
          timeout: DISABLED_QT_TOAST_TIMEOUT,
          ...disabledStateOpts
        });
      return;
    }
    documentStore.resetSecondaryComponent();
    const { isDocumentInLiquidModeView, exitLiquidModeView } = getLiquidModeApis(stores);
    if (isDocumentInLiquidModeView()) {
      exitLiquidModeView();
    }
    quickToolClickHelper(event, { shouldSelectVerbOnClick });
  };

  const verbIcon = verbDetails[verbIndex].icon;
  const verbAriaLabel = verbDetails[verbIndex].ariaLabel;
  const verbTooltip = verbDetails[verbIndex].tooltip;
  const verbLabel = shouldUseMobileModernViewer && verbDetails[verbIndex].label;

  // Theme based Styles
  const getQuickToolStyle = (theme = "LIGHT", state = "default") =>
    isQuickToolDisabled ? QUICK_TOOLS_STYLE.INACTIVE : QUICK_TOOLS_STYLE[theme][state];

  const appTheme = currentTheme === APP_THEMES.DARK ? "DARK" : "LIGHT";
  const defaultStyle = getQuickToolStyle(appTheme, "DEFAULT");
  const hoverStyle = getQuickToolStyle(appTheme, "HOVER");
  const activeStyle = getQuickToolStyle(appTheme, "ACTIVE");
  const selectedStyle = getQuickToolStyle(appTheme, "SELECTED");

  const classList = [];
  if (isActiveQuickTool) {
    classList.push(PrimaryPanelCss.isActiveQuickTool);
  }

  const containerStyle = useMemo(() => (isActiveQuickTool ? { cursor: cursorType || cursors.POINTER } : null), [
    isActiveQuickTool
  ]);

  const tabItemContainerCss = shouldUseMobileModernViewer
    ? classNames(PrimaryPanelCss.MobileTabItemContainer, PrimaryPanelCss.VerbLabel)
    : PrimaryPanelCss.TabItemContainer;

  const quickToolItem = shouldUseMobileModernViewer && (
    <div className={PrimaryPanelCss.mobileQuickToolItem}>
      <CustomIcon url={verbIcon} className={PrimaryPanelCss.mobileQuickToolIcon} />
      <span> {verbLabel} </span>
    </div>
  );

  const getQuickToolButtonProps = () => {
    const buttonProps = {
      id: currentActiveVerb,
      className: classNames(classList),
      ariaLabel: verbAriaLabel,
      tabIndex: isActiveQuickTool ? 0 : -1,
      clickHandler: quickToolClickHandler,
      shouldDisableTooltip: showCaret ? showFlyout : false,
      isSelected: isActiveQuickTool,
      activeStyle,
      hoverStyle,
      cursorType: isActiveQuickTool ? cursorType : cursors.POINTER,
      containerStyle,
      holdAffordance: showCaret,
      ariaDisabled: isQuickToolDisabled
    };

    if (shouldUseMobileModernViewer) {
      buttonProps.buttonClass = PrimaryPanelCss.quickToolButton;
    } else {
      buttonProps.icon = verbIcon;
      buttonProps.selectedStyle = selectedStyle;
      buttonProps.shouldEnableHover = true;
      buttonProps.shouldActivate = true;
      buttonProps.defaultStyle = defaultStyle;
      buttonProps.tooltipString = (isQuickToolDisabled && disabledStateTooltipStr) || verbTooltip;
      buttonProps.tooltipPlacement = TOOLTIP_RIGHT_PLACEMENT;
      buttonProps.tooltipDelay = TOOLTIP_DELAY;
      // If the verb is selected and the tooltip is controlled, then show the tooltip with custom variant
      if (verbDetails[verbIndex].verbId === selectedVerbOnUi && verbDetails[verbIndex].isControlled) {
        buttonProps.tooltipVariant = verbDetails[verbIndex].tooltipVariant;
        buttonProps.isControlled = verbDetails[verbIndex].isControlled;
      }
    }

    return buttonProps;
  };

  return (
    <Fragment>
      <div className={tabItemContainerCss}>
        <ButtonWrapper {...getQuickToolButtonProps()}>{quickToolItem}</ButtonWrapper>
        {showFlyout ? (
          <Popover isDialog={false} className={PrimaryPanelCss.flyoutPopover}>
            <Flyout
              component={flyoutComponent}
              verbDetails={verbDetails}
              setActiveVerb={setActiveVerbCallback}
              closeFlyout={reason => {
                if (reason !== closeFlyoutReasons.PAGE_CLICK && shouldSelectVerbOnClick) {
                  const flyoutTrigger = document.getElementById(getActiveVerb(stores));
                  flyoutTrigger && flyoutTrigger.focus();
                }
                closeFlyout();
              }}
              selectedVerb={selectedVerbOnUi}
              cursorType={isActiveQuickTool ? cursorType : cursors.POINTER}
              forceShowFlyout={forceShowFlyout}
              autoHideTime={flyoutAutoHideTimeout}
              delayTimeOnSelection={QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT}
              initialAutoHide={initialAutoHide}
              shouldShowConditionalToolTip={shouldShowConditionalToolTip}
            />
          </Popover>
        ) : null}
      </div>
    </Fragment>
  );
};

QuickToolItem.propTypes = {
  currentActiveVerb: PropTypes.string.isRequired,
  verbDetails: PropTypes.arrayOf(PropTypes.object).isRequired,
  flyoutComponent: PropTypes.func,
  activeVerb: PropTypes.string.isRequired,
  isQuickToolDisabled: PropTypes.bool,
  groupIndex: PropTypes.number.isRequired,
  disabledStateNotificationMsg: PropTypes.string,
  showCaret: PropTypes.bool.isRequired,
  quickToolClickHelper: PropTypes.func.isRequired,
  flyoutIndex: PropTypes.number,
  closeFlyout: PropTypes.func,
  cursorType: PropTypes.string,
  forceShowFlyout: PropTypes.bool,
  shouldUseMobileModernViewer: PropTypes.bool,
  shouldShowConditionalToolTip: PropTypes.bool
};

QuickToolItem.defaultProps = {
  flyoutComponent: null,
  isQuickToolDisabled: false,
  flyoutIndex: -1,
  disabledStateNotificationMsg: "",
  closeFlyout: () => {},
  cursorType: "default",
  forceShowFlyout: false,
  shouldUseMobileModernViewer: false,
  shouldShowConditionalToolTip: false
};

export { QuickToolItem };
